import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { useOrganization } from './OrganizationContext';
import { useLocation, useNavigate } from 'react-router-dom';
import NoOrganizationPopup from '../components/common/NoOrganizationPopup';

const ModeContext = createContext();

export const useMode = () => {
  const context = useContext(ModeContext);
  if (!context) {
    throw new Error('useMode must be used within a ModeProvider');
  }
  return context;
};

/**
 * ModeProvider - Manages Individual vs Organization vs Admin mode switching
 * 
 * Core Logic:
 * 1. Individual Mode (Default): Personal workspace for ALL users
 * 2. Organization Mode: Only available when user is part of an organization
 * 3. Admin Mode: Only available for users with admin roles
 * 4. Automatic mode detection and access control
 * 5. Individual users NEVER see organization options
 */
export const ModeProvider = ({ children }) => {
  const { currentUser, isEnterpriseUser, loading: authLoading } = useAuth();
  const { currentOrganization, availableOrganizations, loading: orgLoading } = useOrganization();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Mode states - individual | organization | platform_admin | admin (legacy)
  const [currentMode, setCurrentMode] = useState('individual'); // 'individual' | 'organization' | 'platform_admin' | 'admin'
  const [availableModes, setAvailableModes] = useState(['individual']);
  const [isModeSwitching, setIsModeSwitching] = useState(false);
  const [modeHistory, setModeHistory] = useState(['individual']);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Popup states for access control
  const [showNoOrgPopup, setShowNoOrgPopup] = useState(false);

  const organizationMemberships = useMemo(() => {
    const memberships = [];

    if (currentOrganization && currentOrganization._id) {
      memberships.push(currentOrganization);
    }

    if (Array.isArray(availableOrganizations) && availableOrganizations.length > 0) {
      availableOrganizations.forEach(org => {
        if (org && org._id && !memberships.some(existing => existing._id === org._id)) {
          memberships.push(org);
        }
      });
    }

    if (memberships.length === 0 && currentUser?.organizationId) {
      memberships.push({
        _id: currentUser.organizationId,
        name: currentOrganization?.name || null,
        fallback: true
      });
    }

    return memberships;
  }, [availableOrganizations, currentOrganization, currentUser?.organizationId, currentOrganization?.name]);

  // Mode detection from current URL
  useEffect(() => {
    // Wait for auth and org data to load before detecting mode
    if (!authLoading && !orgLoading && currentUser) {
      detectModeFromUrl();
      if (!isInitialized) {
        setIsInitialized(true);
      }
    }
  }, [location.pathname, authLoading, orgLoading, currentUser]);

  // Update available modes based on user's access levels
  useEffect(() => {
    // Wait for auth and org data to load before updating modes
    if (!authLoading && !orgLoading && currentUser) {
      updateAvailableModes();
    }
  }, [currentUser, isEnterpriseUser, organizationMemberships, authLoading, orgLoading]);

  /**
   * Check if user has organization access - STRICT CHECK
   * ONLY users with actual organization membership should have access
   * Platform admins should NOT have organization access (they use Platform Admin instead)
   */
  const hasOrganizationAccess = () => {
    if (!currentUser) {
      console.log('hasOrganizationAccess: No current user');
      return false;
    }

    if (authLoading || orgLoading) {
      console.log('hasOrganizationAccess: Auth or organization context still loading, deferring decision');
      return !!currentUser.organizationId;
    }
    
    console.log('🔍 hasOrganizationAccess: DEBUGGING for user:', {
      email: currentUser.email,
      domain: currentUser.email?.split('@')[1],
      accountType: currentUser.accountType,
      organizationId: currentUser.organizationId,
      orgRole: currentUser.orgRole,
      roles: currentUser.roles,
      platformRole: currentUser.platformRole,
      organizationMembershipsCount: organizationMemberships.length,
      organizationMemberships,
      isEnterpriseUser: isEnterpriseUser,
      isPersonalEmail: isPersonalEmail(currentUser.email)
    });

    // Check if platform admin has organization membership
    // Platform admins WITH organization membership should have access to organization workspace
    // Platform admins WITHOUT organization membership should use Platform Admin only
    if (currentUser.platformRole && ['super_admin', 'platform_admin'].includes(currentUser.platformRole)) {
      const hasOrgMembership = currentUser.organizationId || 
                               organizationMemberships.length > 0 || 
                               (currentUser.orgRole && ['org_admin', 'owner', 'manager', 'employee', 'dept_manager', 'team_lead'].includes(currentUser.orgRole));
      
      if (!hasOrgMembership) {
        console.log('🚫 BLOCKED: Platform admin without org membership should use Platform Admin:', currentUser.platformRole);
        return false;
      }
      // Platform admin WITH org membership - continue to check other conditions
      console.log('✅ Platform admin has org membership, allowing org access check to continue');
    }

    // STRICT CHECK 1: User has explicit organizationId (highest priority - direct membership)
    // This takes precedence over personal email check because user was explicitly added to an organization
    if (currentUser.organizationId && currentUser.organizationId.trim() !== '') {
      console.log('✅ GRANTED: User has organizationId assigned:', currentUser.organizationId);
      return true;
    }
    
    // STRICT CHECK 2: User has valid organization role
    if (currentUser.orgRole && ['org_admin', 'owner', 'manager', 'employee', 'dept_manager', 'team_lead'].includes(currentUser.orgRole)) {
      console.log('✅ GRANTED: User has valid orgRole:', currentUser.orgRole);
      return true;
    }
    
    // STRICT CHECK 3: Organizations array with actual organization membership
    if (organizationMemberships.length > 0) {
      const validOrgs = organizationMemberships.filter(org => org && org._id);
      if (validOrgs.length > 0) {
        console.log('✅ GRANTED: User has organization memberships:', validOrgs.length, 'organizations');
        return true;
      }
    }
    
    // Block personal email domains if no explicit organization membership exists
    // Personal email users without org membership should stay in individual mode
    if (currentUser.email && isPersonalEmail(currentUser.email)) {
      console.log('🚫 BLOCKED: Personal email without org membership:', currentUser.email);
      return false;
    }
    
    // ALL OTHER CASES: DENY ACCESS
    // No fallback checks - user must have explicit organization membership
    console.log('🚫 DENIED: User does not have organization membership');
    console.log('🚫 accountType:', currentUser.accountType, '(not sufficient without org membership)');
    console.log('🚫 isEnterpriseUser:', isEnterpriseUser, '(not sufficient without org membership)');
    return false;
  };

  /**
   * Check if email is a personal email domain
   */
  const isPersonalEmail = (email) => {
    if (!email) return false;
    
    const domain = email.split('@')[1]?.toLowerCase();
    const personalDomains = [
      'gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com', 
      'icloud.com', 'aol.com', 'protonmail.com', 'mail.com',
      'live.com', 'msn.com', 'ymail.com', 'rocketmail.com'
    ];
    
    return personalDomains.includes(domain);
  };



  /**
   * Platform admin access (super_admin / platform_admin)
   */
  const hasPlatformAdminAccess = () => {
    if (!currentUser?.platformRole) return false;
    return ['platform_admin', 'super_admin'].includes(currentUser.platformRole);
  };

  /**
   * Legacy /admin workspace for org admins (not platform admin UI)
   */
  const hasLegacyAdminAccess = () => {
    if (!currentUser) return false;
    if (hasPlatformAdminAccess()) return false;

    const adminRoles = ['admin', 'org_admin'];
    return (
      (Array.isArray(currentUser.roles) && currentUser.roles.some((role) => adminRoles.includes(role))) ||
      currentUser.orgRole === 'org_admin'
    );
  };

  /**
   * Check if user has admin access
   */
  const hasAdminAccess = () => hasPlatformAdminAccess() || hasLegacyAdminAccess();

  /**
   * Detect current mode from URL pattern
   */
  const detectModeFromUrl = () => {
    const path = location.pathname;
    
    // Don't redirect during initial load - let the route protection handle it
    if (!isInitialized) {
      if (path.startsWith('/platform/admin/')) {
        setCurrentMode('platform_admin');
      } else if (path.startsWith('/admin/')) {
        setCurrentMode('admin');
      } else if (path.startsWith('/org/') || path.startsWith('/organization/')) {
        setCurrentMode('organization');
      } else {
        setCurrentMode('individual');
      }
      return;
    }

    if (path.startsWith('/platform/admin/')) {
      if (hasPlatformAdminAccess()) {
        setCurrentMode('platform_admin');
      } else {
        navigate('/dashboard', { replace: true });
      }
    } else if (path.startsWith('/admin/')) {
      // Admin mode - check access
      if (hasAdminAccess()) {
        setCurrentMode('admin');
      } else {
        // Redirect to individual mode if no admin access
        navigate('/dashboard', { replace: true });
      }
    } else if (path.startsWith('/org/') || path.startsWith('/organization/')) {
      // Organization mode - check access
      if (hasOrganizationAccess()) {
        setCurrentMode('organization');
      } else {
        console.log('detectModeFromUrl: Organization access not yet available, awaiting route guard');
      }
    } else {
      setCurrentMode('individual');
    }
  };

  /**
   * Update available modes based on user context
   */
  const updateAvailableModes = () => {
    const modes = ['individual']; // Everyone gets individual mode
    
    console.log('updateAvailableModes: Starting with modes:', modes);
    
    // Add organization mode only if user has organization access
    const hasOrgAccess = hasOrganizationAccess();
    console.log('updateAvailableModes: hasOrganizationAccess result:', hasOrgAccess);
    if (hasOrgAccess) {
      modes.push('organization');
      console.log('updateAvailableModes: Added organization mode');
    }
    
    // Add platform admin workspace for platform roles
    if (hasPlatformAdminAccess()) {
      modes.push('platform_admin');
      console.log('updateAvailableModes: Added platform_admin mode');
    } else if (hasLegacyAdminAccess()) {
      modes.push('admin');
      console.log('updateAvailableModes: Added admin mode');
    }
    
    console.log('updateAvailableModes: Final modes:', modes);
    setAvailableModes(modes);
    
    // If current mode is not available, switch to individual
    if (!modes.includes(currentMode)) {
      console.log('updateAvailableModes: Current mode not available, switching to individual');
      const onOrgPath = location.pathname.startsWith('/org/') || location.pathname.startsWith('/organization/');
      switchToIndividualMode({ navigate: !onOrgPath });
    }
  };

  /**
   * Switch to Individual Mode
   */
  const switchToIndividualMode = async (options = {}) => {
    const { navigate: shouldNavigate = true } = options;

    if (currentMode === 'individual') {
      if (shouldNavigate && location.pathname !== '/dashboard') {
        navigate('/dashboard');
      }
      return;
    }
    
    setIsModeSwitching(true);
    
    try {
      // Update mode state
      setCurrentMode('individual');
      
      // Add to history
      setModeHistory(prev => [...prev.slice(-4), 'individual']);
      
      // Navigate to individual dashboard when allowed
      if (shouldNavigate && location.pathname !== '/dashboard') {
        navigate('/dashboard');
      }
      
    } catch (error) {
      console.error('Error switching to individual mode:', error);
    } finally {
      setTimeout(() => setIsModeSwitching(false), 500);
    }
  };

  /**
   * Switch to Organization Mode
   */
  const switchToOrganizationMode = async (organizationId = null) => {
    if (currentMode === 'organization') return;
    
    // Check if user has access to organization mode
    if (!hasOrganizationAccess() || !availableModes.includes('organization')) {
      console.warn('User does not have access to organization mode');
      return;
    }
    
    setIsModeSwitching(true);
    
    try {
      // Update mode state
      setCurrentMode('organization');
      
      // Add to history
      setModeHistory(prev => [...prev.slice(-4), 'organization']);
      
      // Navigate to organization dashboard
      if (organizationId) {
        // Switch to specific organization if provided
        navigate(`/org/dashboard?orgId=${organizationId}`);
      } else {
        // Use current organization or first available
        navigate('/org/dashboard');
      }
      
    } catch (error) {
      console.error('Error switching to organization mode:', error);
    } finally {
      setTimeout(() => setIsModeSwitching(false), 500);
    }
  };

  /**
   * Switch to Platform Admin Mode
   */
  const switchToPlatformAdminMode = async () => {
    if (currentMode === 'platform_admin') {
      if (location.pathname !== '/platform/admin/dashboard') {
        navigate('/platform/admin/dashboard');
      }
      return;
    }

    if (!hasPlatformAdminAccess() || !availableModes.includes('platform_admin')) {
      console.warn('User does not have access to platform admin mode');
      return;
    }

    setIsModeSwitching(true);

    try {
      setCurrentMode('platform_admin');
      setModeHistory((prev) => [...prev.slice(-4), 'platform_admin']);
      navigate('/platform/admin/dashboard');
    } catch (error) {
      console.error('Error switching to platform admin mode:', error);
    } finally {
      setTimeout(() => setIsModeSwitching(false), 500);
    }
  };

  /**
   * Switch to Admin Mode
   */
  const switchToAdminMode = async () => {
    if (currentMode === 'admin') return;
    
    // Check if user has access to admin mode
    if (!hasLegacyAdminAccess() || !availableModes.includes('admin')) {
      console.warn('User does not have access to admin mode');
      return;
    }
    
    setIsModeSwitching(true);
    
    try {
      // Update mode state
      setCurrentMode('admin');
      
      // Add to history
      setModeHistory(prev => [...prev.slice(-4), 'admin']);
      
      // Navigate to admin dashboard
      navigate('/admin/dashboard');
      
    } catch (error) {
      console.error('Error switching to admin mode:', error);
    } finally {
      setTimeout(() => setIsModeSwitching(false), 500);
    }
  };

  /**
   * Switch to specific mode
   */
  const switchMode = (mode) => {
    switch (mode) {
      case 'individual':
        switchToIndividualMode();
        break;
      case 'organization':
        switchToOrganizationMode();
        break;
      case 'platform_admin':
        switchToPlatformAdminMode();
        break;
      case 'admin':
        switchToAdminMode();
        break;
      default:
        console.warn(`Unknown mode: ${mode}`);
    }
  };

  /**
   * Toggle between available modes (for mode switcher)
   */
  const toggleMode = () => {
    const currentIndex = availableModes.indexOf(currentMode);
    const nextMode = availableModes[(currentIndex + 1) % availableModes.length] || 'individual';
    switchMode(nextMode);
  };

  /**
   * Check if user can access specific mode
   */
  const canAccessMode = (mode) => {
    return availableModes.includes(mode);
  };

  /**
   * Get mode-specific configuration
   */
  const getModeConfig = () => {
    if (currentMode === 'platform_admin') {
      return {
        mode: 'platform_admin',
        title: 'Platform Admin',
        subtitle:
          currentUser?.platformRole === 'super_admin' ? 'Super Administrator' : 'Platform Administrator',
        basePath: '/platform/admin',
        theme: { primary: '#2563eb', accent: '#7c3aed' },
        navigation: 'platform_admin',
        layout: 'platform_admin',
      };
    }

    if (currentMode === 'admin') {
      // Determine admin label based on role and access level
      let adminLabel = 'Admin';
      if (currentUser?.platformRole === 'super_admin') {
        adminLabel = 'Super Admin';
      } else if (currentUser?.platformRole === 'platform_admin') {
        adminLabel = 'Platform Admin';
      } else if (currentUser?.orgRole === 'org_admin') {
        if (currentUser?.orgAdminAccessLevel === 'limited') adminLabel = 'Delegated Admin';
        else if (currentUser?.orgAdminAccessLevel === 'read_only') adminLabel = 'Security Reviewer';
        else adminLabel = 'Full Admin';
      }
      
      return {
        mode: 'admin',
        title: 'Admin Panel',
        subtitle: adminLabel,
        basePath: '/admin',
        theme: { primary: '#dc2626', accent: '#fee2e2' }, // Red theme for admin
        navigation: 'admin',
        layout: 'admin'
      };
    }
    
    if (currentMode === 'organization') {
      return {
        mode: 'organization',
        title: 'Organization Workspace',
        subtitle: currentOrganization?.name || 'Workspace',
        basePath: '/org',
        theme: currentOrganization?.branding || {},
        navigation: 'organization',
        layout: 'organization'
      };
    }
    
    return {
      mode: 'individual',
      title: 'Personal Workspace',
      subtitle: currentUser?.name || 'User',
      basePath: '',
      theme: {},
      navigation: 'individual',
      layout: 'individual'
    };
  };

  /**
   * Get mode-specific routes
   */
  const getModeRoutes = () => {
    if (currentMode === 'platform_admin') {
      return {
        dashboard: '/platform/admin/dashboard',
        users: '/platform/admin/users',
        organizations: '/platform/admin/organizations',
        system: '/platform/admin/system',
        security: '/platform/admin/security',
        analytics: '/platform/admin/analytics',
        settings: '/platform/admin/settings',
        billing: '/platform/admin/billing',
      };
    }

    if (currentMode === 'admin') {
      return {
        dashboard: '/admin/dashboard',
        users: '/admin/users',
        organizations: '/admin/organizations',
        system: '/admin/system',
        security: '/admin/security',
        logs: '/admin/logs',
        settings: '/admin/settings',
        analytics: '/admin/analytics'
      };
    }
    
    if (currentMode === 'organization') {
      return {
        dashboard: '/org/dashboard',
        projects: '/org/projects',
        tasks: '/org/tasks',
        teams: '/org/teams',
        settings: '/org/settings',
        users: '/org/users',
        departments: '/org/departments',
        analytics: '/org/analytics',
        security: '/org/security',
        billing: '/org/billing'
      };
    }
    
    return {
      dashboard: '/dashboard',
      projects: '/projects',
      tasks: '/tasks',
      profile: '/profile',
      settings: '/settings',
      analytics: '/analytics'
    };
  };

  /**
   * Check if current route is valid for current mode
   */
  const isValidRouteForMode = (path = location.pathname) => {
    if (currentMode === 'platform_admin') {
      return path.startsWith('/platform/admin/');
    }

    if (currentMode === 'admin') {
      return path.startsWith('/admin/');
    }
    
    if (currentMode === 'organization') {
      return path.startsWith('/org/') || path.startsWith('/organization/');
    }
    
    return (
      !path.startsWith('/org/') &&
      !path.startsWith('/organization/') &&
      !path.startsWith('/admin/') &&
      !path.startsWith('/platform/admin/')
    );
  };

  // Helper functions for components
  const isIndividualUser = () => {
    return !hasOrganizationAccess() && !hasAdminAccess();
  };

  const canSwitchToOrganization = () => {
    return hasOrganizationAccess();
  };

  const canAccessAdmin = () => hasLegacyAdminAccess();

  const canAccessPlatformAdmin = () => hasPlatformAdminAccess();

  // Popup management for access control
  const showOrganizationAccessDenied = () => {
    setShowNoOrgPopup(true);
  };

  const hideOrganizationAccessDenied = () => {
    setShowNoOrgPopup(false);
  };

  // Attempt to switch to organization mode with access check
  const tryAccessOrganizationMode = () => {
    if (hasOrganizationAccess()) {
      switchToOrganizationMode();
      return true;
    } else {
      console.log('tryAccessOrganizationMode: Access denied - showing popup');
      showOrganizationAccessDenied();
      return false;
    }
  };

  const value = {
    // Mode state
    currentMode,
    availableModes,
    isModeSwitching,
    modeHistory,
    
    // Mode switching functions
    switchToIndividualMode,
    switchToOrganizationMode,
    switchToPlatformAdminMode,
    switchToAdminMode,
    switchMode,
    toggleMode,
    
    // Mode utilities
    canAccessMode,
    getModeConfig,
    getModeRoutes,
    isValidRouteForMode,
    
    // Access control functions  
    hasOrganizationAccess,
    hasPlatformAdminAccess,
    hasLegacyAdminAccess,
    hasAdminAccess,
    isIndividualUser,
    canSwitchToOrganization,
    canAccessAdmin,
    canAccessPlatformAdmin,
    tryAccessOrganizationMode,
    
    // Popup management
    showNoOrgPopup,
    showOrganizationAccessDenied,
    hideOrganizationAccessDenied,
    
    // Mode information
    isIndividualMode: currentMode === 'individual',
    isOrganizationMode: currentMode === 'organization',
    isPlatformAdminMode: currentMode === 'platform_admin',
    isAdminMode: currentMode === 'admin',
    canSwitchModes: availableModes.length > 1,

    // Debug / metadata
    organizationMemberships
  };

  return (
    <ModeContext.Provider value={value}>
      {children}
      {/* Organization Access Denied Popup */}
      <NoOrganizationPopup 
        isOpen={showNoOrgPopup}
        onClose={hideOrganizationAccessDenied}
        trigger="mode_switch"
      />
    </ModeContext.Provider>
  );
};

export default ModeContext;